import * as React from 'react';
import { StartTransaction } from './start-transaction';
import { getTransactionQueryOptions, useTransaction } from '../api/get-transaction';
import { TransactionPending } from './transaction-pending';
import { TransactionStatus } from '@/types/api/bankid/v2/transaction-status.enum';
import { TransactionCompleted } from './transaction-completed';
import { TransactionFailed } from './transaction-failed';
import { TransactionExpired } from './transaction-expired';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionErrorCode } from '@/types/api/bankid/v2/transaction-error-code.enum';
import { useQueryParams } from '@/hooks/shared/use-query-params';
import { useNavigate } from 'react-router-dom';

export function Transaction({ onCompleted }: { onCompleted?: () => void }) {
  const redirect = useQueryParams().getQueryParamByKey('redirect');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showQR, setShowQR] = React.useState<boolean>(false);
  const [transactionId, setTransactionId] = React.useState<string | null>(null);
  const { data: transaction, isError } = useTransaction({
    transactionId: transactionId ?? '',
    queryConfig: { refetchInterval: (query) => query.state.data?.status === TransactionStatus.Pending && 2000 },
  });

  const reset = () => {
    if (!transactionId) return;
    queryClient.removeQueries({ queryKey: getTransactionQueryOptions(transactionId).queryKey });
    setTransactionId(null);
  };

  React.useEffect(() => {
    if (transaction?.status === TransactionStatus.Completed) {
      if (typeof onCompleted === 'function') {
        onCompleted();
      } else if (redirect) {
        const url = new URL(redirect, window.location.origin);
        url.searchParams.set('bankid', 'done');
        navigate(`${url.pathname}${url.search}${url.hash}`);
      }
    }
  }, [transaction?.status]);

  if (isError) {
    return (
      <div className="mx-auto flex flex-col items-center gap-4">
        <TransactionFailed errorCode={TransactionErrorCode.Unknown} reset={reset} />
      </div>
    );
  }

  return (
    <div className="mx-auto flex flex-col items-center gap-4">
      {!transaction && <StartTransaction setTransactionId={setTransactionId} setShowQR={setShowQR} showQR={showQR} />}
      {transaction?.status === TransactionStatus.Pending && (
        <TransactionPending transaction={transaction} showQR={showQR} reset={reset} />
      )}
      {transaction?.status === TransactionStatus.Completed && <TransactionCompleted />}
      {transaction?.status === TransactionStatus.Failed && (
        <TransactionFailed errorCode={transaction.errorCode} reset={reset} />
      )}
      {transaction?.status === TransactionStatus.Expired && <TransactionExpired reset={reset} />}
    </div>
  );
}
